import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Dialog from "../../components/ui/Dialog";
import Input from "../../components/ui/Input";
import DataTable, {
  Column,
  DataTableFilterDef,
} from "../../components/ui/DataTable";
import { adminsService } from "../../services/admins.service";
import { AdminRecord, CreateAdminDto, UpdateAdminDto } from "../../dtos/admin.dto";
import { roleService } from "../../services/role.service";
import { Role } from "../../interfaces/role.interface";
import { Permission } from "../../permissions/permission.enum";
import { useAuth } from "../../context/AuthContext";
import { UserRole } from "../../enums/user-role.enum";
import {
  createAdminSchema,
  updateAdminSchema,
} from "./validations/admin.schema";

const AdminsList = () => {
  const { t } = useTranslation("common");
  const { hasPermission, user: currentUser } = useAuth();

  const [items, setItems] = useState<AdminRecord[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminRecord | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(editing ? updateAdminSchema : createAdminSchema),
  });

  const canCreate = hasPermission(Permission.ADMINS_CREATE);
  const canUpdate = hasPermission(Permission.ADMINS_UPDATE);
  const canDelete = hasPermission(Permission.ADMINS_DELETE);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [list, rolesList] = await Promise.all([
        adminsService.list(),
        roleService.findAll(),
      ]);
      setItems(list);
      setRoles(rolesList);
    } catch (e: any) {
      setError(e?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => {
    setEditing(null);
    setSubmitError(null);
    reset({
      email: "",
      password: "",
      confirmPassword: "",
      role: UserRole.ADMIN,
      firstName: "",
      lastName: "",
      isActive: true,
      notifyOnRegistration: false,
    });
    setDialogOpen(true);
  };

  const openEdit = (admin: AdminRecord) => {
    setEditing(admin);
    setSubmitError(null);
    reset({
      email: admin.email || "",
      password: "",
      confirmPassword: "",
      role: (admin.role as UserRole) || UserRole.ADMIN,
      firstName: admin.firstName || "",
      lastName: admin.lastName || "",
      isActive: admin.isActive ?? true,
      notifyOnRegistration: admin.notifyOnRegistration ?? false,
    });
    setDialogOpen(true);
  };

  const onSubmit = async (data: any) => {
    setSubmitError(null);
    try {
      if (editing) {
        const payload: UpdateAdminDto = {
          email: data.email,
          role: data.role,
          firstName: data.firstName,
          lastName: data.lastName,
          isActive: data.isActive,
          notifyOnRegistration: data.notifyOnRegistration,
        };
        if (data.password) payload.password = data.password;
        await adminsService.update(editing.id, payload);
      } else {
        const payload: CreateAdminDto = {
          email: data.email,
          password: data.password,
          role: data.role,
          firstName: data.firstName,
          lastName: data.lastName,
          isActive: data.isActive,
          notifyOnRegistration: data.notifyOnRegistration,
        };
        await adminsService.create(payload);
      }
      setDialogOpen(false);
      await refresh();
    } catch (e: any) {
      setSubmitError(e?.message || "Failed to save admin");
    }
  };

  const remove = async (id: string) => {
    setError("");
    try {
      await adminsService.remove(id);
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Failed to delete admin");
    }
  };

  const columns: Column<AdminRecord>[] = [
    {
      key: "email",
      header: "Email",
      className: "font-medium text-gray-900 dark:text-gray-100",
    },
    {
      key: "role",
      header: "Role",
      render: (u) => String(u.role),
    },
    {
      key: "name",
      header: "Name",
      render: (u) => [u.firstName, u.lastName].filter(Boolean).join(" "),
    },
    {
      key: "isActive",
      header: "Active",
      render: (u) => (u.isActive ? "Yes" : "No"),
    },
    {
      key: "notifyOnRegistration",
      header: t("admin_notify_on_registration_short"),
      render: (u) => (u.notifyOnRegistration ? "Yes" : "No"),
    },
    {
      key: "actions",
      header: "Actions",
      className: "text-right",
      render: (u) => (
        <div className="space-x-2">
          {canUpdate && (
            <Button size="sm" variant="outline" onClick={() => openEdit(u)}>
              Edit
            </Button>
          )}
          {canDelete && currentUser?.id !== u.id && (
            <Button size="sm" variant="danger" onClick={() => remove(u.id)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  const filterDefs: DataTableFilterDef[] = [
    {
      key: "isActive",
      label: "Status",
      type: "select",
      options: [
        { label: "Active", value: "true" },
        { label: "Inactive", value: "false" },
      ],
    },
  ];

  return (
    <Layout title={t("admins")}>
      <div className="space-y-6 h-full flex flex-col">
        <Card className="p-4 shadow-sm flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {t("admins")}
            </h1>
            {canCreate && (
              <div className="flex items-center gap-2">
                <Button onClick={openCreate} variant="primary">
                  + {t("admins")}
                </Button>
              </div>
            )}
          </div>
        </Card>

        {error && (
          <div className="p-3 rounded-md bg-red-50 text-red-600 border border-red-100">
            {error}
          </div>
        )}

        <DataTable
          className="flex-1 min-h-0"
          columns={columns}
          data={items}
          loading={loading}
          filterDefs={filterDefs}
          emptyMessage={t("noData")}
        />

        <Dialog
          open={dialogOpen}
          title={editing ? "Edit Admin" : "Create Admin"}
          onClose={() => setDialogOpen(false)}
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            {submitError && (
              <div className="p-3 rounded-md bg-red-50 text-red-600 border border-red-100 text-sm">
                {submitError}
              </div>
            )}

            <Input
              label="Email"
              {...register("email")}
              error={errors.email?.message}
            />

            {!editing && (
              <>
                <Input
                  label="Password"
                  type="password"
                  {...register("password")}
                  error={errors.password?.message}
                />
                <Input
                  label="Confirm Password"
                  type="password"
                  {...register("confirmPassword")}
                  error={errors.confirmPassword?.message}
                />
              </>
            )}

            {editing && (
              <>
                <div className="text-xs text-gray-500 mt-2 mb-1">
                  Leave blank to keep current password
                </div>
                <Input
                  label="New Password"
                  type="password"
                  {...register("password")}
                  error={errors.password?.message}
                />
                <Input
                  label="Confirm New Password"
                  type="password"
                  {...register("confirmPassword")}
                  error={errors.confirmPassword?.message}
                />
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <select
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register("role")}
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.name}>
                    {role.name}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.role.message}
                </p>
              )}
            </div>

            <Input
              label="First Name"
              {...register("firstName")}
              error={errors.firstName?.message}
            />
            <Input
              label="Last Name"
              {...register("lastName")}
              error={errors.lastName?.message}
            />

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                {...register("isActive")}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <label
                htmlFor="isActive"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Active
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="notifyOnRegistration"
                {...register("notifyOnRegistration")}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <label
                htmlFor="notifyOnRegistration"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("admin_notify_on_registration")}
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                {t("cancel")}
              </Button>
              <Button type="submit">{t("save")}</Button>
            </div>
          </form>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminsList;
